/**
 * Utility for managing BAC API call timings in localStorage.
 * Stores the last 10 successful call timings and calculates average for progress estimation.
 */

const STORAGE_KEY = 'bac_api_timings';
const MAX_STORED_TIMINGS = 10;
const EXPIRY_DAYS = 10;
const DEFAULT_AVERAGE_MS = 8000; // 8 seconds default

const getBrowserStorage = () => {
  if (typeof window === 'undefined') return null;
  const storage = window.localStorage;
  if (!storage) return null;
  if (
    typeof storage.getItem !== 'function' ||
    typeof storage.setItem !== 'function' ||
    typeof storage.removeItem !== 'function'
  ) {
    return null;
  }
  return storage;
};

/**
 * Get stored timings from localStorage
 * @returns {Array<{timestamp: number, durationMs: number}>}
 */
const getStoredTimings = () => {
  const storage = getBrowserStorage();
  if (!storage) return [];
  try {
    const stored = storage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch (error) {
    console.warn('[BAC Timing] Failed to read stored timings:', error);
    return [];
  }
};

/**
 * Save timings to localStorage
 * @param {Array<{timestamp: number, durationMs: number}>} timings
 */
const saveTimings = (timings) => {
  const storage = getBrowserStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(timings));
  } catch (error) {
    console.warn('[BAC Timing] Failed to save timings:', error);
  }
};

/**
 * Remove expired timings (older than EXPIRY_DAYS)
 * @param {Array<{timestamp: number, durationMs: number}>} timings
 * @returns {Array<{timestamp: number, durationMs: number}>}
 */
const filterExpiredTimings = (timings) => {
  const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  const now = Date.now();
  return timings.filter((timing) => {
    const age = now - timing.timestamp;
    return age < expiryMs;
  });
};

/**
 * Add a new timing entry (only for successful calls)
 * @param {number} durationMs - Duration of the API call in milliseconds
 */
export const recordSuccessfulCall = (durationMs) => {
  if (!durationMs || durationMs <= 0 || !Number.isFinite(durationMs)) {
    console.warn('[BAC Timing] Invalid duration:', durationMs);
    return;
  }

  const timings = getStoredTimings();
  const filtered = filterExpiredTimings(timings);

  // Add new timing
  filtered.push({
    timestamp: Date.now(),
    durationMs: Math.round(durationMs),
  });

  // Sort by timestamp descending and keep only the last MAX_STORED_TIMINGS
  filtered.sort((a, b) => b.timestamp - a.timestamp);
  const trimmed = filtered.slice(0, MAX_STORED_TIMINGS);

  saveTimings(trimmed);
};

/**
 * Get the average duration from stored timings
 * @returns {number} Average duration in milliseconds, or DEFAULT_AVERAGE_MS if no data
 */
export const getAverageDuration = () => {
  const timings = getStoredTimings();
  const filtered = filterExpiredTimings(timings);

  if (filtered.length === 0) {
    return DEFAULT_AVERAGE_MS;
  }

  const sum = filtered.reduce((acc, timing) => acc + timing.durationMs, 0);
  const average = sum / filtered.length;
  return Math.round(average);
};

/**
 * Get the maximum duration from stored timings (for dynamic estimate updates)
 * @returns {number} Maximum duration in milliseconds, or DEFAULT_AVERAGE_MS if no data
 */
export const getMaxDuration = () => {
  const timings = getStoredTimings();
  const filtered = filterExpiredTimings(timings);

  if (filtered.length === 0) {
    return DEFAULT_AVERAGE_MS;
  }

  const max = Math.max(...filtered.map((t) => t.durationMs));
  return max;
};

/**
 * Clear all stored timings (useful for testing or reset)
 */
export const clearStoredTimings = () => {
  const storage = getBrowserStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[BAC Timing] Failed to clear timings:', error);
  }
};
